import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiDownload,
  FiMoreVertical,
  FiClock,
  FiTrash2,
  FiEdit3,
  FiEye,
  FiFile,
  FiImage,
  FiCode,
  FiDatabase,
  FiFileText,
  FiArchive,
} from "react-icons/fi";
import {
  formatFileSize,
  getFileTypeDisplayName,
  timeAgo,
} from "../../../utils/fileUtils";
import FilePreviewModal from "./FilePreviewModal";
import "./FileCard.css";

const FileCard = ({
  file,
  roomId,
  userRole,
  currentUserId,
  viewMode = "grid",
  onUpdate,
  onDelete,
  onViewVersions,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const canDelete =
    userRole?.isLeader || file.uploadedBy?._id === currentUserId;

  const getFileIcon = (type) => {
    const icons = {
      image: FiImage,
      code: FiCode,
      dataset: FiDatabase,
      document: FiFileText,
      pdf: FiFile,
      archive: FiArchive,
    };
    return icons[type] || FiFile;
  };

  const getTypeColor = (type) => {
    const colors = {
      image: { bg: "#fef3c7", color: "#d97706" },
      code: { bg: "#dbeafe", color: "#2563eb" },
      dataset: { bg: "#d1fae5", color: "#059669" },
      document: { bg: "#ede9fe", color: "#7c3aed" },
      pdf: { bg: "#fee2e2", color: "#dc2626" },
      archive: { bg: "#f3e8ff", color: "#9333ea" },
    };
    return colors[type] || { bg: "#f1f5f9", color: "#64748b" };
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (file.downloadUrl) {
        const link = document.createElement("a");
        link.href = file.downloadUrl;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        await onUpdate?.();
      }
    } catch (error) { /* ignored */ } finally {
      setDownloading(false);
      setShowMenu(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${file.fileName}"?`)) return;
    try {
      await onDelete?.(file._id);
    } catch (error) { /* ignored */ }
    setShowMenu(false);
  };

  const IconComponent = getFileIcon(file.fileType);
  const typeColor = getTypeColor(file.fileType);

  return (
    <>
      <motion.div
        className={`rfc-card ${viewMode === "list" ? "rfc-card--list" : ""}`}
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        {/* Thumbnail / Icon Area */}
        <div
          className="rfc-thumbnail"
          style={{ background: typeColor.bg }}
          onClick={() => setShowPreview(true)}
        >
          {file.thumbnailUrl ? (
            <img src={file.thumbnailUrl} alt={file.fileName} />
          ) : (
            <IconComponent size={viewMode === "list" ? 24 : 40} color={typeColor.color} />
          )}
          {file.version > 1 && (
            <span className="rfc-version">v{file.version}</span>
          )}
        </div>

        {/* Content */}
        <div className="rfc-content">
          <h3 className="rfc-name" title={file.fileName}>
            {file.fileName}
          </h3>

          <div className="rfc-meta">
            <span
              className="rfc-type-badge"
              style={{ background: typeColor.bg, color: typeColor.color }}
            >
              {getFileTypeDisplayName(file.fileType)}
            </span>
            <span className="rfc-size">
              {file.fileSizeFormatted || formatFileSize(file.fileSize)}
            </span>
          </div>

          <div className="rfc-info">
            <span className="rfc-uploader">
              {file.uploadedBy?.name || "Unknown"}
            </span>
            <span className="rfc-time">{timeAgo(file.createdAt)}</span>
          </div>

          {file.tags && file.tags.length > 0 && viewMode !== "list" && (
            <div className="rfc-tags">
              {file.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="rfc-tag">{tag}</span>
              ))}
              {file.tags.length > 3 && (
                <span className="rfc-tag rfc-tag--more">+{file.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="rfc-actions">
          <button
            className="rfc-btn rfc-btn--preview"
            onClick={() => setShowPreview(true)}
            title="Preview"
          >
            <FiEye size={16} />
          </button>

          {file.version > 1 && (
            <button
              className="rfc-btn rfc-btn--versions"
              onClick={() => onViewVersions?.(file._id)}
              title="Version history"
            >
              <FiClock size={16} />
            </button>
          )}

          <div className="rfc-menu-wrapper">
            <button
              className="rfc-btn rfc-btn--menu"
              onClick={() => setShowMenu(!showMenu)}
            >
              <FiMoreVertical size={16} />
            </button>

            <AnimatePresence>
              {showMenu && (
                <>
                  <div
                    className="rfc-menu-backdrop"
                    onClick={() => setShowMenu(false)}
                  />
                  <motion.div
                    className="rfc-menu"
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  >
                    <button
                      className="rfc-menu-item"
                      onClick={handleDownload}
                      disabled={downloading}
                    >
                      <FiDownload size={16} />
                      {downloading ? "Downloading..." : "Download"}
                    </button>
                    {file.uploadedBy?._id === currentUserId && (
                      <button className="rfc-menu-item">
                        <FiEdit3 size={16} />
                        Edit Details
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="rfc-menu-item rfc-menu-item--danger"
                        onClick={handleDelete}
                      >
                        <FiTrash2 size={16} />
                        Delete
                      </button>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <FilePreviewModal
            file={file}
            onClose={() => setShowPreview(false)}
            onDownload={handleDownload}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FileCard;
